<script setup lang="ts">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import { useStation, CustomMessageType } from '@/store/station';
import logo_url from '@/assets/logo.png'

import CardAccess from "./card-access.vue";
import UserAccess from "./user-access.vue";
import OfflineOverlay from "../common/overlays/offline-overlay.vue";

const station = useStation();
const { name } = useDisplay();
// TODO:
// const route = useRoute();
// const router = useRouter();
const { t, locale } = useI18n();

const custom_text_width = computed(() => ["xs", "sm"].includes(name.value) ? "w-100" : "w-50");
const card_enabled = computed(() => station.accept_guests);
const evse_id = computed((): string => station.evse_id);
const has_logo = computed(() => !! station.logo);
const custom_message = computed((): string => {
  const message: CustomMessageType | null = station.custom_message;
  if (message === null) { return ""; }
  const fallback_locale = message.fallback_locale;
  const loc = ((): string => {
    if (!(locale.value in message.text) || message.text[locale.value].length === 0) {
      return fallback_locale;
    }
    return locale.value;
  })();
  if (! (loc in message.text)) {
    console.error("[login_screen.custom_message]: default locale does not have a text");
    console.log("default locale: ", loc)
    console.log(JSON.stringify(message.text))
    return "";
  }
  return message.text[loc];
});
const has_custom_message = computed(() => (custom_message.value ?? "").length > 0);

</script>

<template>
  <v-container class="h-100">
    <offline-overlay />
    <v-col class="align-start">
      <v-row v-if="has_custom_message" class="pt-1 pb-3 justify-center">
        <v-card elevation="0" :color="`white ${custom_text_width} h-sm-100 h-xs-100 justify-center`">
          <v-card-text class="text-center custom-message text-pre-wrap text-info">
            {{ custom_message }}
          </v-card-text>
        </v-card>
      </v-row>

      <v-row v-if="card_enabled" class="pa-1 justify-center">
        <card-access />
      </v-row>

      <v-row
        v-if="card_enabled"
        class="pt-4 pb-2 justify-space-around align-center font-weight-bold text-wrap text-info"
      >
        {{ t("or") }}
      </v-row>

      <v-row>
        <user-access />
      </v-row>

      <v-row class="mt-5 pt-0 pb-0 justify-center">
        <v-card elevation="0" :color="`white ${custom_text_width} h-sm-100 h-xs-100 justify-center`">
          <v-card-text class="text-center evse-id text-pre-wrap text-info">
            {{ t("evse_id") }}: {{ evse_id }}
          </v-card-text>
        </v-card>
      </v-row>

      <v-spacer />
      <v-expand-transition mode="out-in">
        <v-row v-if="has_logo" class="align-center justify-center mt-3 text-wrap">
          <b class="text-info mr-1">powered by </b>
          <v-img class="brandLogoS ml-1 mb-3" :src="logo_url" />
        </v-row>
      </v-expand-transition>
    </v-col>
  </v-container>
</template>

<i18n lang="yml">
en:
  or: or
  evse_id: "Serial ID"
it:
  or: oppure
  evse_id: "ID Seriale"
fr:
  or: ou
  evse_id: "Numéro de serie"
de:
  or: oder
  evse_id: "Serien-ID"
nl:
  or: of
  evse_id: "Serienummer"
es:
  or: o
  evse_id: "ID de serie"
</i18n>

<style scoped lang="scss">
.brandLogoS {
  max-width: 9em;
  position: relative;
  top: 5px;
  object-fit: contain;
}
.custom-message {
  font-size: 16px;
  font-weight: bold;
}
.evse-id {
  font-size: 12px;
  font-weight: bold;
}
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
